import React from "react"
import { graphql } from "gatsby"
import { Box, Container, Flex, Heading } from "@theme-ui/components"
import loadable from "@loadable/component"
import Layout from "../components/layout"
import { getPagePath } from "../utils/path"
import { HelmetDatoCms } from "gatsby-source-datocms"
import TitleAndBody from "../components/blocks/titleAndBody"
import PageCarousel from "../components/blocks/pageCarousel"
import Image from "../components/blocks/image"
import ImageGallery from "../components/blocks/imageGallery"
import OrderedList from "../components/blocks/orderedList"
import ItemCarousel from "../components/blocks/itemCarousel"
import Accordion from "../components/blocks/accordion"
import DocumentCollection from "../components/blocks/documentCollection"
import Embed from "../components/blocks/embed"
import PageHero from "./pageHero"
import ImageAndText from "../components/blocks/imageAndText"
import NumbersCollection from "../components/blocks/numbersCollections"
import ContactForm from "../components/blocks/contactForm"
import ContactSection from "../components/blocks/contactSection"
import Categories from "../components/blocks/categoryBlock"
import StoryList from "../components/blocks/storyList"
import { useFavicon } from "../hooks/useFavicon"
import WebinarCollection from "../components/blocks/webinarCollection"
import RichContentStructuredText from "../components/richContentStructuredText"
import JobForm from "../components/blocks/jobForm"
import JobList from "../components/blocks/jobList"
import BrochureEmbed from "../components/blocks/brochureEmbed"
import ArticleCollection from "../components/blocks/articleCollection"

const LocationsMap = loadable(
  () => import("../components/blocks/locationMap"),
  { ssr: false }
)

const Page = ({ data: { page, site, contactSection } }) => {
  const favicon = useFavicon().site.faviconMetaTags

  console.log(page.metaKeywords)

  const pageAllSlugLocales = page._allSlugLocales.sort(function (a, b) {
    return site.locales.indexOf(a.locale) - site.locales.indexOf(b.locale)
  })

  const i18nPaths = pageAllSlugLocales.map(locale => {
    return {
      locale: locale.locale,
      value: getPagePath(page, locale.locale),
    }
  })

  return (
    <Layout locale={page.locale} i18nPaths={i18nPaths}>
      <HelmetDatoCms seo={page.seoMetaTags} favicon={favicon}>
        <html lang={page.locale} />
        <meta name="keywords" content={page.metaKeywords} />
      </HelmetDatoCms>
      <PageHero page={page} image={page.heroImage} icon={page.extendedIcon} />
      {page.content.map(block => (
        <Box as="section" key={block.id}>
          {block.model.apiKey === "article_collection" && (
            <ArticleCollection block={block} theme="light" />
          )}
          {block.model.apiKey === "title_and_body" && (
            <TitleAndBody block={block} />
          )}
          {block.model.apiKey === "ordered_list" && (
            <OrderedList
              title={block.title}
              subtitle={block.subtitle}
              body={block.body}
            />
          )}
          {block.model.apiKey === "page_carousel" && (
            <PageCarousel title={block.title} pages={block.pages} />
          )}
          {block.model.apiKey === "item_carousel" && (
            <ItemCarousel items={block.items} />
          )}
          {block.model.apiKey === "accordion" && (
            <Accordion title={block.title} items={block.items} />
          )}
          {block.model.apiKey === "document_collection" && (
            <DocumentCollection
              title={block.title}
              documents={block.documents}
              showPublicationDate={block.showPublicationDate}
            />
          )}
          {block.model.apiKey === "image" && <Image image={block.image} />}
          {block.model.apiKey === "locations_map" && (
            <LocationsMap locations={block.locations} />
          )}
          {block.model.apiKey === "contact_form" && (
            <Box sx={{ backgroundColor: "#fafafa" }}>
              <ContactForm block={block} />
            </Box>
          )}
          {block.model.apiKey === "embed" && (
            <Embed
              title={block.title}
              code={block.code}
              fullWidth={block.fullWidth}
            />
          )}
          {block.model.apiKey === "brochure_embed" && (
            <Container>
              <BrochureEmbed code={block.code} />
            </Container>
          )}
          {block.model.apiKey === "image_gallery" && (
            <Container>
              <ImageGallery images={block.images} />
            </Container>
          )}
          {block.model.apiKey === "numbers_collection" && (
            <NumbersCollection
              title={block.title}
              image={block.image}
              numbers={block.numbers}
            />
          )}
          {block.model.apiKey === "image_and_text" && (
            <ImageAndText block={block} />
          )}
          {block.model.apiKey === "story_list" && <StoryList block={block} />}
          {block.model.apiKey === "category" && (
            <Categories
              page={page}
              title={block.title}
              description={block.description}
            />
          )}
          {block.model.apiKey === "webinar_collection" && (
            <WebinarCollection block={block} />
          )}
          {block.model.apiKey === "job_list" && (
            <JobList block={block} pageTitle={page.title} />
          )}
          {block.model.apiKey === "job_form" && <JobForm block={block} />}
          {page.customContactSection &&
            block.model.apiKey === "custom_contact_section" && (
              <ContactSection data={{ ...block, body: block.secondBody }} />
            )}
          {block.model.apiKey === "title_and_body_group" && (
            <>
              <Box
                sx={{
                  py: [6, 8],
                  backgroundColor: "primary",
                }}
              >
                <Container>
                  <Flex
                    sx={{
                      width: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    {block.contentOne.map(elem => (
                      <Box
                        sx={{
                          maxWidth: "366px",
                          "*": {
                            color: "light",
                          },
                        }}
                      >
                        <Heading
                          color="light"
                          as="h2"
                          variant="h3"
                          sx={{
                            mt: [0, 0],
                            fontWeight: "300",
                          }}
                        >
                          {elem.content.title}
                        </Heading>
                        <Box
                          sx={{
                            p: {
                              color: "light",
                            },
                          }}
                        >
                          <RichContentStructuredText text={elem.content.body} />
                        </Box>
                      </Box>
                    ))}
                  </Flex>
                </Container>
              </Box>
            </>
          )}
        </Box>
      ))}
      {contactSection && !page.customContactSection && (
        <ContactSection data={contactSection.nodes[0]} />
      )}
    </Layout>
  )
}

export default Page

export const query = graphql`
  query PageQuery($id: String!, $locale: String!) {
    site: datoCmsSite {
      locales
    }
    contactSection: allDatoCmsContactSection(
      filter: { locale: { eq: $locale } }
    ) {
      nodes {
        title
        subtitle
        body {
          links {
            __typename
            ... on DatoCmsInternalLink {
              id: originalId
              anchor
              locale
              model {
                apiKey
              }
              link {
                ... on DatoCmsBlogPage {
                  ...BlogDetails
                }
                ... on DatoCmsPage {
                  ...PageDetails
                  ...PageTreeParent
                  ...AllSlugLocales
                }
                ... on DatoCmsArticle {
                  ...ArticleDetails
                  ...ArticleAllSlugLocales
                }
                ... on DatoCmsArticleCategory {
                  ...ArticleCategoryDetails
                  ...ArticleCategoryAllSlugLocales
                }
              }
            }
          }
          value
        }
      }
    }
    page: datoCmsPage(id: { eq: $id }) {
      customContactSection
      metaKeywords
      ...PageDetails
      ...PageTreeParent
      ...AllSlugLocales
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
      content {
        ... on DatoCmsArticleCollection {
          id
          title
          model {
            apiKey
          }
          articles {
            ...ArticleDetails
            ...ArticleAllSlugLocales
            ...ArticleMeta
          }
        }
        ... on DatoCmsTitleAndBodyGroup {
          id
          contentOne: content {
            ... on DatoCmsTitleAndBody {
              id
              background
              content {
                ... on DatoCmsRichContent {
                  ...RichContent
                }
              }
              model {
                apiKey
              }
            }
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsWebinarCollection {
          id
          title
          webinars {
            ... on DatoCmsSingleWebinar {
              id
              video {
                ... on DatoCmsVideoLink {
                  link
                }
              }
              title
              body {
                value
                blocks {
                  __typename
                  ... on DatoCmsEmbed {
                    id: originalId
                    ...EmbedDetails
                  }
                }
                links {
                  __typename
                  ... on DatoCmsInternalLink {
                    id: originalId
                    anchor
                    locale
                    model {
                      apiKey
                    }
                    link {
                      ... on DatoCmsBlogPage {
                        ...BlogDetails
                      }
                      ... on DatoCmsPage {
                        ...PageDetails
                        ...PageTreeParent
                        ...AllSlugLocales
                      }
                      ... on DatoCmsArticle {
                        ...ArticleDetails
                        ...ArticleAllSlugLocales
                      }
                      ... on DatoCmsArticleCategory {
                        ...ArticleCategoryDetails
                        ...ArticleCategoryAllSlugLocales
                      }
                    }
                  }
                }
              }
            }
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsCustomContactSection {
          id
          title
          subtitle
          secondBody: body {
            value
            links {
              __typename
              ... on DatoCmsInternalLink {
                id: originalId
                anchor
                locale
                model {
                  apiKey
                }
                link {
                  ... on DatoCmsBlogPage {
                    ...BlogDetails
                  }
                  ... on DatoCmsPage {
                    ...PageDetails
                    ...PageTreeParent
                    ...AllSlugLocales
                  }
                  ... on DatoCmsArticle {
                    ...ArticleDetails
                    ...ArticleAllSlugLocales
                  }
                  ... on DatoCmsArticleCategory {
                    ...ArticleCategoryDetails
                    ...ArticleCategoryAllSlugLocales
                  }
                }
              }
            }
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsNumbersCollection {
          id
          title
          image {
            gatsbyImageData(
              width: 1480
              placeholder: BLURRED
              forceBlurhash: false
            )
          }
          numbers {
            legend
            float
            suffix
            prefix
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsJobList {
          id
          title
          jobs {
            id
            title
            description
          }
          description
          model {
            apiKey
          }
        }
        ... on DatoCmsJobForm {
          id
          kicker
          title
          subtitle
          description
          privacyPolicyDescription
          model {
            apiKey
          }
        }
        ... on DatoCmsContactForm {
          id
          title
          subtitle
          description
          alternateLayout
          link {
            ... on DatoCmsInternalLink {
              id: originalId
              anchor
              locale
              model {
                apiKey
              }
              link {
                ... on DatoCmsBlogPage {
                  ...BlogDetails
                }
                ... on DatoCmsPage {
                  ...PageDetails
                  ...PageTreeParent
                  ...AllSlugLocales
                }
                ... on DatoCmsArticle {
                  ...ArticleDetails
                  ...ArticleAllSlugLocales
                }
                ... on DatoCmsArticleCategory {
                  ...ArticleCategoryDetails
                  ...ArticleCategoryAllSlugLocales
                }
              }
            }
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsEmbed {
          id
          ...EmbedDetails
        }
        ... on DatoCmsBrochureEmbed {
          id
          code
          model {
            apiKey
          }
        }
        ... on DatoCmsImageAndText {
          id
          content {
            ... on DatoCmsRichContent {
              ...RichContent
            }
          }
          image {
            gatsbyImageData(
              width: 1920
              placeholder: BLURRED
              forceBlurhash: false
            )
          }
          rightAligned
          fullBackground
          model {
            apiKey
          }
        }
        ... on DatoCmsStoryList {
          body {
            value
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsImage {
          id
          image {
            gatsbyImageData(
              width: 1480
              placeholder: BLURRED
              forceBlurhash: false
            )
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsCategory {
          id
          title
          description
          model {
            apiKey
          }
        }
        ... on DatoCmsLocationsMap {
          id
          locations {
            originalId
            id
            name
            addressCountry
            addressRegion
            addressLocality
            email
            faxNumber
            coordinates {
              latitude
              longitude
            }
            postalCode
            streetAddress
            telephone
            locale
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsImageGallery {
          id
          ...ImageGallery
        }
        ... on DatoCmsDocumentCollection {
          id
          title
          showPublicationDate
          documents {
            title
            subtitle
            documents {
              url
              title
              format
            }
            meta {
              firstPublishedAt(locale: $locale, formatString: "DD MMMM Y")
            }
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsAccordion {
          id
          title
          items: content {
            ... on DatoCmsRichContent {
              ...RichContent
            }
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsTitleAndBody {
          id
          background
          content {
            ... on DatoCmsRichContent {
              ...RichContent
            }
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsOrderedList {
          id
          title
          subtitle
          body {
            blocks
            links
            value
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsPageCarousel {
          id
          title
          pages {
            ... on DatoCmsPage {
              ...PageDetails
              ...PageTreeParent
              ...AllSlugLocales
            }
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsItemCarousel {
          id
          items {
            ... on DatoCmsRichContent {
              ...RichContent
            }
          }
          model {
            apiKey
          }
        }
      }
    }
  }

  fragment RichContent on DatoCmsRichContent {
    title
    label
    subtitle
    image {
      gatsbyImageData(width: 1480, placeholder: BLURRED, forceBlurhash: false)
    }
    body {
      blocks {
        __typename
        ... on DatoCmsDoubleImage {
          id: originalId
          images {
            gatsbyImageData(
              width: 1920
              placeholder: BLURRED
              forceBlurhash: false
            )
          }
          model {
            apiKey
          }
        }
        ... on DatoCmsImageGallery {
          id: originalId
          ...ImageGallery
        }
        ... on DatoCmsEmbed {
          id: originalId
          ...EmbedDetails
        }
        ... on DatoCmsBrochureEmbed {
          id: originalId
          code
          model {
            apiKey
          }
        }
        ... on DatoCmsNumbersGroup {
          id: originalId
          numbers {
            legend
            float
            suffix
            prefix
          }
        }
      }
      links {
        __typename
        ... on DatoCmsInternalLink {
          id: originalId
          anchor
          locale
          model {
            apiKey
          }
          link {
            ... on DatoCmsBlogPage {
              ...BlogDetails
            }
            ... on DatoCmsPage {
              ...PageDetails
              ...PageTreeParent
              ...AllSlugLocales
            }
            ... on DatoCmsArticle {
              ...ArticleDetails
              ...ArticleAllSlugLocales
            }
            ... on DatoCmsArticleCategory {
              ...ArticleCategoryDetails
              ...ArticleCategoryAllSlugLocales
            }
          }
        }
        ... on DatoCmsExternalLink {
          id: originalId
          anchor
          locale
          model {
            apiKey
          }
        }
      }
      value
    }
    model {
      apiKey
    }
  }

  fragment AllSlugLocales on DatoCmsPage {
    _allSlugLocales {
      value
      locale
    }
  }

  fragment PageDetails on DatoCmsPage {
    id
    locale
    shortDescription {
      value
    }
    title
    slug
    root
    body {
      value
    }
    model {
      apiKey
    }
    icon {
      url
    }
    extendedIcon {
      url
    }
    heroImage {
      url
      mimeType
      gatsbyImageData(width: 1920, placeholder: NONE, forceBlurhash: false)
      video {
        streamingUrl
        thumbnailUrl(format: jpg)
        mp4Url(exactRes: low)
        muxPlaybackId
      }
    }
  }

  fragment EmbedDetails on DatoCmsEmbed {
    code
    fullWidth
    model {
      apiKey
    }
  }

  fragment PageTreeParent on DatoCmsPage {
    treeParent {
      id
      title
      slug
      root
      locale
      ...AllSlugLocales
      treeParent {
        id
        title
        slug
        root
        locale
        ...AllSlugLocales
      }
    }
  }
`
